import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Landmark, Save } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [{ k: "today", l: "Aujourd'hui" }, { k: "week", l: "Semaine" }, { k: "month", l: "Mois" }, { k: "year", l: "Année" }, { k: "all", l: "Tout" }];

export default function Fiscalite() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rate, setRate] = useState(16);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/finance/tax?period=${period}`).then((r) => { setData(r.data); setRate(r.data.rate); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [period]);

  const saveRate = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { tax_rate: Number(rate) });
      toast.success("Taux d'imposition enregistré");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div><h1 className="text-2xl sm:text-3xl font-bold font-heading">Fiscalité</h1><p className="text-slate-500 text-sm mt-1">Estimation de l'impôt sur le chiffre d'affaires</p></div>
        <div className="flex gap-1 bg-white border rounded-xl p-1">
          {PERIODS.map((p) => <button key={p.k} data-testid={`fisc-period-${p.k}`} onClick={() => setPeriod(p.k)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${period === p.k ? "bg-brand-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{p.l}</button>)}
        </div>
      </div>

      <div className="bg-white border rounded-2xl p-5">
        <Label className="flex items-center gap-2 mb-2"><Landmark className="w-4 h-4 text-brand-600" /> Taux d'imposition (%)</Label>
        <div className="flex gap-2 max-w-xs">
          <Input data-testid="fisc-rate" type="number" value={rate} onChange={(e) => setRate(e.target.value)} />
          <Button data-testid="fisc-save" onClick={saveRate} disabled={saving} className="bg-brand-600 hover:bg-brand-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
          </Button>
        </div>
      </div>

      {loading || !data ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div> : (
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="bg-white border rounded-2xl p-5"><div className="text-sm text-slate-500">Chiffre d'affaires</div><div data-testid="fisc-ca" className="text-2xl font-bold font-heading mt-1">{money(data.ca)}</div></div>
          <div className="bg-white border rounded-2xl p-5"><div className="text-sm text-slate-500">Taux appliqué</div><div className="text-2xl font-bold font-heading mt-1">{data.rate}%</div></div>
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5"><div className="text-sm text-amber-700">Impôt estimé (sur CA)</div><div data-testid="fisc-tax" className="text-2xl font-bold font-heading text-amber-700 mt-1">{money(data.tax_estimate)}</div></div>
        </div>
      )}
      <p className="text-xs text-slate-400">Estimation indicative basée sur le chiffre d'affaires de la période. À adapter selon votre régime fiscal.</p>
    </div>
  );
}
